import DemoBanner from "./DemoBanner";
import { Truck, Package, Clock, Shield, ChevronRight, Star } from "lucide-react";

const steps = [
  { num: "01", title: "Get a Quote", desc: "Tell us where you're moving and when. Free, instant online quotes." },
  { num: "02", title: "We Pack & Load", desc: "Our crew arrives on time, handles your stuff with care, and loads up." },
  { num: "03", title: "Relax & Unpack", desc: "We deliver to your new place, set things down where you want them. Done." },
];

export default function AtlasMoving() {
  return (
    <div data-testid="demo-atlas-moving" style={{ fontFamily: "'Manrope', sans-serif", background: "#FFFFFF" }}>
      {/* Nav */}
      <nav className="px-6 md:px-16 py-5 flex items-center justify-between bg-white">
        <div className="flex items-center gap-2">
          <div className="bg-[#0C1F3F] text-[#FFC940] w-9 h-9 rounded-lg flex items-center justify-center">
            <Truck size={20} />
          </div>
          <span className="text-xl md:text-2xl font-extrabold text-[#0C1F3F] tracking-tight">
            Atlas <span className="text-[#2E86DE]">Moving</span> Co.
          </span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm font-bold text-[#0C1F3F]/60">
          <a href="#how" className="hover:text-[#0C1F3F] transition-colors">How It Works</a>
          <a href="#services" className="hover:text-[#0C1F3F] transition-colors">Services</a>
          <a href="#quote" className="hover:text-[#0C1F3F] transition-colors">Contact</a>
          <button className="bg-[#FFC940] text-[#0C1F3F] px-6 py-2.5 rounded-xl font-extrabold text-sm hover:bg-[#ffbe1a] transition-colors">
            Get a Quote
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-16 pt-16 pb-24 md:pt-24 md:pb-36 bg-gradient-to-b from-[#EBF3FD] to-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FFC940]/20 text-[#0C1F3F] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              <Star size={14} fill="#FFC940" stroke="#FFC940" /> Rated #1 Local Movers
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#0C1F3F] leading-[1.05] tracking-tight">
              Moving
              <br />
              <span className="text-[#2E86DE]">made simple.</span>
            </h1>
            <p className="text-lg text-[#0C1F3F]/50 mt-6 max-w-md leading-relaxed font-medium">
              Whether it's across town or across the state, Atlas gets you there — on time, on budget, stress-free.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button className="bg-[#0C1F3F] text-white px-8 py-4 rounded-xl font-extrabold text-sm hover:bg-[#2E86DE] transition-colors flex items-center gap-2">
                Get Your Free Quote <ChevronRight size={18} />
              </button>
              <button className="border-2 border-[#0C1F3F]/15 text-[#0C1F3F] px-8 py-4 rounded-xl font-bold text-sm hover:border-[#0C1F3F]/40 transition-colors">
                Our Services
              </button>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-[#0C1F3F]/40 font-semibold">
              <span className="flex items-center gap-1"><Shield size={16} className="text-[#2E86DE]" /> Fully Insured</span>
              <span className="flex items-center gap-1"><Clock size={16} className="text-[#2E86DE]" /> Same-Week Available</span>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1758523671893-0ba21cf4260f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGZhbWlseSUyMG5ldyUyMGhvbWUlMjBtb3ZpbmclMjBkYXl8ZW58MHx8fHwxNzc2MjEyNTMwfDA&ixlib=rb-4.1.0&q=85"
                alt="Happy couple in new home"
                className="w-full h-[400px] md:h-[480px] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#FFC940] text-[#0C1F3F] p-5 rounded-2xl shadow-lg">
              <p className="text-3xl font-extrabold">2,400+</p>
              <p className="text-sm font-bold opacity-70">Successful Moves</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="py-24 md:py-32 px-6 md:px-16 bg-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#2E86DE] text-sm font-bold uppercase tracking-widest mb-2">Dead Simple</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0C1F3F] tracking-tight mb-16">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s) => (
              <div key={s.num} className="relative">
                <span className="text-7xl font-extrabold text-[#2E86DE]/10">{s.num}</span>
                <h3 className="text-xl font-extrabold text-[#0C1F3F] -mt-4">{s.title}</h3>
                <p className="text-[#0C1F3F]/50 mt-3 leading-relaxed font-medium">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 md:py-32 px-6 md:px-16 bg-[#0C1F3F]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#FFC940] text-sm font-bold uppercase tracking-widest mb-2">Our Services</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-14">
            We handle it all.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Truck, title: "Local Moves", desc: "Same-city moves, done in a day." },
              { icon: Package, title: "Packing Services", desc: "We pack it so you don't have to." },
              { icon: Shield, title: "Long Distance", desc: "State-to-state with tracking." },
              { icon: Clock, title: "Last Minute", desc: "Need to move ASAP? We got you." },
            ].map((s) => (
              <div key={s.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors">
                <s.icon size={28} className="text-[#FFC940]" />
                <h3 className="text-lg font-bold text-white mt-4">{s.title}</h3>
                <p className="text-white/50 text-sm mt-2 font-medium">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="quote" className="py-24 md:py-32 px-6 md:px-16 bg-[#FFC940] text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0C1F3F] tracking-tight">
          Ready to make your move?
        </h2>
        <p className="text-[#0C1F3F]/60 mt-4 text-lg max-w-md mx-auto font-medium">
          Free quotes in under 2 minutes. No obligations, no surprises.
        </p>
        <button className="mt-8 bg-[#0C1F3F] text-white px-10 py-4 rounded-xl font-extrabold text-sm hover:bg-[#2E86DE] transition-colors">
          Get Your Free Quote
        </button>
      </section>

      {/* Footer */}
      <footer className="bg-[#0C1F3F] py-8 px-6 md:px-16 text-center">
        <span className="text-white/30 text-sm font-medium">{new Date().getFullYear()} Atlas Moving Co. All rights reserved.</span>
      </footer>

      <div className="pb-16" />
      <DemoBanner />
    </div>
  );
}
